import { useState } from "react";
import { Compass, Copy, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTEXTES_AUDIT, labelContexte, isRestructuration } from "@/lib/checklist";
import { dupliquerDossier } from "@/lib/dossiers";

/** LOT 30 — Contexte verrouillé après création + clonage vers un nouveau contexte. */
export default function ContexteMaster({ dossierId, contexte, contexteAutre }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [target, setTarget] = useState("");
    const [autre, setAutre] = useState("");
    const [opts, setOpts] = useState({ dataroom: true, questionnaire: true, previsionnel: true });

    async function dupliquer() {
        if (!target) return toast.error("Choisissez un nouveau contexte");
        if (target === "autre" && !autre.trim()) return toast.error("Précisez le contexte");
        setBusy(true);
        try {
            const r = await dupliquerDossier(dossierId, {
                contexte_audit: target, contexte_autre: target === "autre" ? autre.trim() : null,
                reprendre_dataroom: opts.dataroom, reprendre_questionnaire: opts.questionnaire, reprendre_previsionnel: opts.previsionnel,
            });
            toast.success("Dossier dupliqué", { description: `${r.documents_copies} document(s) repris.` });
            setOpen(false);
            navigate(`/dossiers/${r.id}`);
        } catch (e) { toast.error("Duplication impossible", { description: e.message }); }
        finally { setBusy(false); }
    }

    return (
        <section data-testid="contexte-master" className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-indigo-600 shadow-sm"><Compass className="h-5 w-5" strokeWidth={1.75} /></span>
                <div className="min-w-[220px] flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-indigo-700">Contexte de la mission</p>
                    <p data-testid="contexte-master-value" className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                        <Lock className="h-3.5 w-3.5 text-slate-400" /> {labelContexte(contexte)}{contexteAutre ? ` — ${contexteAutre}` : ""}
                    </p>
                    <p className="text-xs text-slate-600">Verrouillé après création. Pour changer de contexte, dupliquez le dossier.</p>
                </div>
                <Button type="button" variant="outline" data-testid="dupliquer-dossier-button" onClick={() => setOpen(true)} className="rounded-full border-indigo-300 text-indigo-700 hover:bg-white">
                    <Copy className="h-4 w-4" strokeWidth={1.75} /> Dupliquer / Changer de contexte
                </Button>
            </div>
            {isRestructuration(contexte) && <p data-testid="contexte-master-prev-hint" className="mt-3 text-xs font-medium text-indigo-800">Procédure collective : Prévisionnel, Mémo Flash et rapport « Premier RDV AJ » disponibles.</p>}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="dupliquer-dialog" className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Dupliquer le dossier</DialogTitle>
                        <DialogDescription>Crée une nouvelle mission pour la même entreprise avec un nouveau contexte. Choisissez les éléments à reprendre.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Nouveau contexte de la mission</label>
                            <Select value={target} onValueChange={setTarget}>
                                <SelectTrigger data-testid="dupliquer-contexte-select" className="h-11 bg-card"><SelectValue placeholder="Sélectionner le contexte…" /></SelectTrigger>
                                <SelectContent>{CONTEXTES_AUDIT.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`dupliquer-contexte-${c.value}`}>{c.label}</SelectItem>)}</SelectContent>
                            </Select>
                            {target === "autre" && <Input data-testid="dupliquer-contexte-autre" value={autre} onChange={(e) => setAutre(e.target.value)} placeholder="Précisez…" className="h-10 bg-card" />}
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-foreground">Éléments à reprendre</p>
                            {[["dataroom", "Reprendre la Dataroom (copie des fichiers)"], ["questionnaire", "Reprendre le Questionnaire"], ["previsionnel", "Reprendre le Prévisionnel"]].map(([k, lbl]) => (
                                <label key={k} className="flex items-center gap-2 text-sm text-foreground cursor-pointer select-none">
                                    <Checkbox data-testid={`dupliquer-opt-${k}`} checked={opts[k]} onCheckedChange={(v) => setOpts((o) => ({ ...o, [k]: !!v }))} /> {lbl}
                                </label>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">Annuler</Button>
                        <Button data-testid="dupliquer-confirm" onClick={dupliquer} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Créer le dossier dupliqué</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
